import { GridSortItem } from "@mui/x-data-grid";
import { useState } from "react";
import { useParams } from "react-router-dom";
import CustomDataGridV2 from "../../../../../../../components/CustomDataGridV2";
import RenderWithTooltip from "../../../../../../../components/RenderWithTooltip";
import ActionMenu from "../../../../../../../components/app/ActionMenu";
import DeleteModal, {
  useDeleteDisClosure,
} from "../../../../../../../components/app/DeleteModal";
import { useRoleController } from "../../../../../../../components/useRoleController";
import useDisclosure from "../../../../../../../hooks/use-disclosure";
import { usePastMedicalHistoryList } from "../../../../../../../queries/history/past-medication/use-patient-medical-history-list";
import { useArchivePatientMedicineHistory } from "../../../../../../../queries/history/past-medication/use-patient-medication-history-archive";
import { MedicalHistory } from "../../../../../../../services";
import { formatDate } from "../../../../../../../utils/date-time-utility";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  applyDefaultColumnSettings,
  filterActionCol,
  roleFilter,
} from "../../../../../../../utils/utils";
import UpsertMedicalHistoryDrawer from "./UpsertMedicalHistoryDrawer";
import { getStoredRealm } from "@/utils/auth-storage";

const PastMedicalHistoryNew = () => {
  const isVisitNotePage = window.location.pathname.includes("/visit-note");
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);
  const { patientId } = useParams() as { patientId: any };
  const { hasReadOnly, hasUpdate, hasDelete } = useRoleController(
    "Past Medical History"
  );

  const { mutateAsync: archiveMedicineHistory } =
    useArchivePatientMedicineHistory();

  const medicationDisclosure = useDisclosure(false);

  const {
    data: medicalHistories,
    refetch,
    isFetching,
  } = usePastMedicalHistoryList({
    patientUuid: patientId,
    pagination,
    gridSorting,
    archive: false,
    xTENANTID: getStoredRealm() ?? undefined,
  });

  const deletePastMedicationDisclosure = useDeleteDisClosure();

  const COLUMNS = applyDefaultColumnSettings<MedicalHistory>(
    [
      {
        field: "conditionName",
        headerName: "Condition",
        flex: 0.35,
        minWidth: 120,
        renderCell: ({ value }) => <RenderWithTooltip value={value} />,
      },
      {
        field: "medical_history_date",
        headerName: "Onset Date",
        flex: 0.25,
        minWidth: 90,
        renderCell: ({ row }) =>
          row?.medicalHistoryDate ? formatDate(row?.medicalHistoryDate) : "-",
      },
      {
        field: "note",
        headerName: "Note",
        flex: 0.6,
        minWidth: 150,
        renderCell: ({ value }) => <RenderWithTooltip value={value} />,
      },
      {
        field: "recordedDate",
        headerName: "Recorded Date",
        flex: 0.25,
        minWidth: 100,
        renderCell: ({ value }) => (value ? formatDate(value) : "-"),
      },
      {
        field: "Action",
        headerName: "Actions",
        width: 70,
        align: "center",
        headerAlign: "center",
        hideSortIcons: true,
        renderCell: ({ row }) => {
          return (
            <ActionMenu
              options={roleFilter({
                list: [
                  { value: "edit", label: "Edit" },
                  { value: "delete", label: "Delete" },
                ],
                compWith: {
                  edit: hasUpdate,
                  delete: hasDelete,
                },
              })}
              onActionClick={(v) => {
                if (v === "edit") {
                  medicationDisclosure.open(row);
                } else deletePastMedicationDisclosure.open(row?.uuid);
              }}
            />
          );
        },
      },
    ],
    ["medical_history_date", "recordedDate"]
  );

  function archiveHandler<T>(value: boolean, id: T) {
    return archiveMedicineHistory({
      patientMedicalHistoryId: id?.toString() ?? "",
      status: value,
      xTENANTID: getStoredRealm() ?? undefined,
    }).then((res) => {
      refetch();
      return res;
    });
  }

  return (
    <>
      <UpsertMedicalHistoryDrawer disclosureHost={medicationDisclosure} />

      <DeleteModal
        w="380px"
        open={deletePastMedicationDisclosure.opened}
        onClose={deletePastMedicationDisclosure.close}
        id={deletePastMedicationDisclosure.deleteId}
        onConfirmation={(id) => archiveHandler(true, id)}
        content={"You want to delete this Past Medical History"}
      />

      {/* <ChartTableWrapper> */}
      <CustomDataGridV2
        getRowId={(row) => row?.uuid}
        loading={isFetching}
        rows={medicalHistories?.data?.content ?? []}
        rowCount={medicalHistories?.data?.page?.totalElements ?? 10}
        columns={filterActionCol(COLUMNS, hasReadOnly || isVisitNotePage)}
        disableRowSelectionOnClick
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        onSortModelChange={setGridSorting}
        sortModel={gridSorting}
      />
      {/* </ChartTableWrapper> */}
    </>
  );
};

export default PastMedicalHistoryNew;
